import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
} from "@mui/material";
import { Post } from "components/molecules/post";
import { Media, Pricing, SentimentPricing } from "core/types/social.types";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "components/atoms/text";
import { DatePicker } from "@mui/x-date-pickers";
import { useNavigate } from "react-router-dom";
import { EmptyState } from "components/atoms/empty-state";
import ArrowCircleRightRoundedIcon from "@mui/icons-material/ArrowCircleRightRounded";
import ArrowCircleLeftRoundedIcon from "@mui/icons-material/ArrowCircleLeftRounded";
import SentimentSatisfiedRoundedIcon from "@mui/icons-material/SentimentSatisfiedRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface Props {
  instagramId: string;
  postList?: Media[];
  isPostLoading: boolean;
  isPricingLoading: boolean;
  pricingObject?: Pricing;
  reportSuccess?: string;
  page: number;
  lastPage: boolean;
  setPage: (n: number) => void;
  calculatePrice: (instagramId: string, posts: string[]) => void;
  sendRequest: (
    posts: string[],
    pricingRequestId: string,
    igPageId: string,
    level: number
  ) => void;
}

const SocialDashboard: FunctionComponent<Props> = ({
  instagramId,
  postList,
  isPostLoading,
  isPricingLoading,
  pricingObject,
  page,
  lastPage,
  setPage,
  calculatePrice,
  sendRequest,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedPosts, setSelectedPosts] = useState<Media[]>([]);
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [activeStep, setActiveStep] = useState(0);
  const [open, setOpen] = useState(false);
  const [tokenSum, setTokenSum] = useState(0);
  const [selectedLevel, setSelectedLevel] = useState<number>();

  useEffect(() => {
    setSelectedPosts([]);
    setStartDate(undefined);
    setEndDate(undefined);
    setActiveStep(0);
    setTokenSum(0);
    setSelectedLevel(undefined);
  }, [instagramId]);

  const handleClickPost = (post: Media) => {
    var temporaryList: Media[] = [...selectedPosts];
    const selectedSentimentPricing = post.sentimentPricing?.find(
      (sp) => sp.level === selectedLevel
    );
    const alreadyAdded = temporaryList.find((p) => p.id === post.id);
    if (alreadyAdded) {
      temporaryList = temporaryList.filter((p) => p.id !== post.id);
      if (activeStep === 1 && selectedSentimentPricing) {
        setTokenSum(tokenSum - selectedSentimentPricing?.CoDeTokens);
      }
    } else {
      temporaryList.push(post);
      if (activeStep === 1 && selectedSentimentPricing) {
        setTokenSum(tokenSum + selectedSentimentPricing?.CoDeTokens);
      }
    }
    setSelectedPosts(temporaryList);
  };

  useEffect(() => {
    if (selectedLevel) {
      let newTokenSum = 0;
      selectedPosts.forEach((sp) => {
        const choosedLevel = sp.sentimentPricing?.find(
          (sentiment) => sentiment.level === selectedLevel
        );
        if (choosedLevel) {
          newTokenSum = newTokenSum + choosedLevel?.CoDeTokens;
        }
      });
      setTokenSum(newTokenSum);
    }
  }, [selectedLevel]);

  const handleStartDateChange = (date: Date | null) => {
    if (date) setStartDate(date);
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) setEndDate(date);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (startDate && endDate) {
        const postsByDate = postList?.filter(
          (post) =>
            new Date(post.timestamp) >= startDate &&
            new Date(post.timestamp) <= endDate
        );
        if (postsByDate && postsByDate?.length > 0) {
          calculatePrice(
            instagramId,
            postsByDate.map((p) => p.id)
          );
          setActiveStep(1);
        } else {
          setOpen(true);
        }
      } else {
        calculatePrice(
          instagramId,
          selectedPosts.map((p) => p.id)
        );
        setActiveStep(1);
      }
      setSelectedPosts([]);
    } else if (activeStep === 1) {
      if (selectedPosts.length > 0 && pricingObject && selectedLevel) {
        sendRequest(
          selectedPosts.map((p) => p.id),
          pricingObject.id,
          instagramId,
          selectedLevel
        );
        setActiveStep(2);
      }
    }
  };

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleDisabled = () => {
    if (activeStep === 0) {
      if (startDate && endDate) {
        const postsByDate = postList?.filter(
          (post) =>
            new Date(post.timestamp) >= startDate &&
            new Date(post.timestamp) <= endDate
        );
        if (postsByDate && postsByDate?.length > 0) {
          return false;
        } else {
          return true;
        }
      } else if (selectedPosts.length > 0) {
        return false;
      } else {
        return true;
      }
    } else if (activeStep === 1) {
      if (selectedPosts.length > 0 && pricingObject && selectedLevel) {
        return false;
      } else {
        return true;
      }
    }
  };

  const getTextToShow = (value: number) => {
    switch (value) {
      case 1:
        return "Basic";
      case 2:
        return "Premium";
      case 3:
        return "Premium Plus";
      default:
        return "Basic";
    }
  };

  return (
    <>
      {activeStep === 0 && (
        <Grid
          container
          spacing={2}
          display={"flex"}
          alignItems={"flex-end"}
          pb={"20px"}
        >
          <Grid item xs={12} sm={3}>
            <Text fontSize={"15px"} pt={"20px"} textAlign={"center"}>
              {t("social-dashboard.select-for-date")}
            </Text>
          </Grid>
          <Grid item container xs={12} sm={7} spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <DatePicker
                  value={startDate}
                  onChange={handleStartDateChange}
                  slotProps={{
                    textField: {
                      variant: "standard",
                      label: t("social-dashboard.form.start_date"),
                      fullWidth: true,
                      InputLabelProps: {
                        style: {
                          width: "80%",
                          fontSize: "14px !important",
                          fontWeight: "700",
                        },
                      },
                      style: {
                        minWidth: "200px",
                      },
                    },
                  }}
                  sx={{
                    svg: { color: "rgba(0, 0, 0, 0.87)" },
                    "&.MuiPickersToolbar": {
                      backgroundColor: "#424242",
                    },
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <DatePicker
                  value={endDate}
                  onChange={handleEndDateChange}
                  slotProps={{
                    textField: {
                      variant: "standard",
                      label: t("social-dashboard.form.end_date"),
                      fullWidth: true,
                      InputLabelProps: {
                        style: {
                          width: "80%",
                          fontSize: "14px !important",
                          fontWeight: "700",
                        },
                      },
                      style: {
                        minWidth: "200px",
                      },
                    },
                  }}
                  sx={{
                    svg: { color: "rgba(0, 0, 0, 0.87)" },
                    "&.MuiPickersToolbar": {
                      backgroundColor: "#424242",
                    },
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="outlined"
              onClick={() => handleNext()}
              disabled={!startDate || !endDate}
              fullWidth
            >
              {t("social-dashboard.form.select")}
            </Button>
          </Grid>
        </Grid>
      )}
      {activeStep === 1 && pricingObject && (
        <>
          <Grid container>
            {pricingObject?.posts.length !== 0 && (
              <Grid item xs={12} sm={6} md={4} mt={"20px"}>
                <FormControl
                  fullWidth
                  variant="standard"
                  sx={{ marginBottom: "26px" }}
                >
                  <InputLabel
                    id="accounts-label"
                    sx={{ fontSize: "14px !important", fontWeight: "700" }}
                  >
                    {t("social-dashboard.select_level")}
                  </InputLabel>
                  <Select
                    labelId="select-level-label"
                    id="select-level-select"
                    value={selectedLevel ? selectedLevel : undefined}
                    MenuProps={MenuProps}
                    color="secondary"
                    renderValue={(value) => <Text>{getTextToShow(value)}</Text>}
                  >
                    <MenuItem value={1} onClick={() => setSelectedLevel(1)}>
                      <ListItem>
                        <ListItemIcon>
                          <SentimentSatisfiedRoundedIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={getTextToShow(1)}
                          secondary="AI sentiment"
                        />
                      </ListItem>
                    </MenuItem>
                    <MenuItem value={2} onClick={() => setSelectedLevel(2)}>
                      <ListItem>
                        <ListItemIcon>
                          <BoltRoundedIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={getTextToShow(2)}
                          secondary="chatGPT3.5"
                        />
                      </ListItem>
                    </MenuItem>
                    <MenuItem value={3} onClick={() => setSelectedLevel(3)}>
                      <ListItem>
                        <ListItemIcon>
                          <AutoAwesomeOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={getTextToShow(3)}
                          secondary="chatGPT4"
                        />
                      </ListItem>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
          <Grid
            container
            sx={{
              marginBottom: "20px",
            }}
            display={"flex"}
            flexDirection={"column"}
            alignItems={"center"}
          >
            <Grid
              item
              xs={12}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
              mb={"10px"}
              mt={"20px"}
            >
              <Text fontSize={"18px"}>
                {t("social-dashboard.remaining_tokens")}
                <b>{pricingObject?.userTokens}</b>
              </Text>
            </Grid>
            <Button
              variant="outlined"
              onClick={() => {
                if (pricingObject) {
                  if (selectedPosts.length === 0) {
                    setSelectedPosts(pricingObject.posts);
                    let sum = 0;
                    pricingObject.posts.forEach((element) => {
                      const selectedSentimentPricing =
                        element.sentimentPricing?.find(
                          (sp) => sp.level === selectedLevel
                        );
                      if (selectedSentimentPricing) {
                        sum += selectedSentimentPricing.CoDeTokens;
                      }
                    });
                    setTokenSum(sum);
                  } else {
                    setSelectedPosts([]);
                    setTokenSum(0);
                  }
                }
              }}
              disabled={!selectedLevel}
            >
              {selectedPosts.length > 0
                ? t("social-dashboard.deselect_all")
                : t("social-dashboard.select_all")}
            </Button>
          </Grid>
        </>
      )}
      {isPostLoading || isPricingLoading ? (
        <div
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <CircularProgress color="secondary" size={"100px"} />
        </div>
      ) : (
        <>
          {activeStep === 0 && (
            <>
              <Text fontSize={"15px"} paddingY={"15px"} textAlign={"center"}>
                {t("social-dashboard.select-by-click")}
              </Text>
              <Grid
                container
                spacing={2}
                display={"flex"}
                justifyContent={"center"}
              >
                {postList?.map((post, idx) => (
                  <Grid item key={idx}>
                    <Post
                      post={post}
                      selectedPosts={selectedPosts}
                      handleClickPost={handleClickPost}
                    />
                  </Grid>
                ))}
              </Grid>
              <Grid container display={"flex"} justifyContent={"space-between"}>
                <Grid item xs={6}>
                  <IconButton
                    onClick={() => page > 1 && setPage(page - 1)}
                    disabled={page <= 1}
                  >
                    <ArrowCircleLeftRoundedIcon
                      color={page <= 1 ? "disabled" : "primary"}
                    />
                  </IconButton>
                </Grid>
                <Grid item xs={6} display={"flex"} justifyContent={"flex-end"}>
                  <IconButton
                    onClick={() => setPage(page + 1)}
                    disabled={lastPage}
                  >
                    <ArrowCircleRightRoundedIcon
                      color={lastPage ? "disabled" : "primary"}
                    />
                  </IconButton>
                </Grid>
              </Grid>
            </>
          )}
          {activeStep === 1 && (
            <Grid
              container
              spacing={2}
              display={"flex"}
              justifyContent={"center"}
            >
              {pricingObject?.posts.length === 0 && (
                <Text textAlign={"center"}>
                  {t("empty_states.pricing.title")}
                </Text>
              )}
              {pricingObject?.posts?.map((post, idx) => (
                <Grid item key={idx}>
                  <Post
                    post={post}
                    selectedPosts={selectedPosts}
                    handleClickPost={handleClickPost}
                    sentiment={post.sentimentPricing?.find(
                      (sp) => sp.level === selectedLevel
                    )}
                    pricingMode
                  />
                </Grid>
              ))}
            </Grid>
          )}
          {activeStep === 2 && (
            <Grid container alignItems={"center"} direction={"column"}>
              <EmptyState
                title={t("empty_states.successful_report.title")}
                description={t("empty_states.successful_report.subtitle")}
                styleTitle={{ fontWeight: "700" }}
              />
            </Grid>
          )}
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            width={"100%"}
            py={"20px"}
          >
            {activeStep === 0 && (
              <Button
                variant="contained"
                onClick={() => handleNext()}
                disabled={handleDisabled()}
              >
                {t("social-dashboard.form.next")}
              </Button>
            )}

            <>
              {pricingObject && activeStep === 1 && (
                <>
                  <Text mr={"10px"} fontSize={"18px"}>
                    {t("social-dashboard.paying_tokens")}
                    <b
                      style={{
                        color:
                          pricingObject?.userTokens < tokenSum
                            ? "red"
                            : "black",
                      }}
                    >
                      {tokenSum}
                    </b>
                  </Text>
                  {pricingObject?.userTokens < tokenSum ? (
                    <Button
                      variant="text"
                      sx={{
                        ml: "10px",
                      }}
                      onClick={() => navigate("/subscription-plan")}
                    >
                      {t("social-dashboard.renew_plan")}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={() => handleNext()}
                      disabled={handleDisabled()}
                    >
                      {t("social-dashboard.form.next")}
                    </Button>
                  )}
                </>
              )}
            </>

            {activeStep === 2 && (
              <Button
                variant="contained"
                onClick={() => navigate("/social/my-social-reports")}
              >
                {t("social-dashboard.navigate_to_report")}
              </Button>
            )}
          </Box>
        </>
      )}
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error" sx={{ width: "100%" }}>
          {t("social-dashboard.form.error")}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SocialDashboard;
